import {ShapeItem} from '@PosterWhiteboard/items/shape-item/shape-item.class';
import type {CopyableItemStyles, InitItemOpts, ItemObject, ShapeItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {util} from '@postermywall/fabricjs-2';
import {FillSurface} from '@PosterWhiteboard/classes/fill-surface';
import type {DeepPartial} from '@/global';
import {PMWFabricLine} from '@PosterWhiteboard/items/line-item/pmw-fabric-line';
import type {LineCapStyle} from '@PosterWhiteboard/items/line-item/line-item.types';
import {DEFAULT_LINE_WIDTH, LineDashType} from '@PosterWhiteboard/items/line-item/line-item.types';

const DEFAULT_HEIGHT = 24;
const LINE_ITEM_PADDING = 15;

export interface LineItemObject extends ShapeItemObject {
  lineDashType: LineDashType;
  lineCapStart: LineCapStyle;
  lineCapEnd: LineCapStyle;
  customGapSize: number;
  customDashSize: number;
  isRounded: boolean;
}

export class LineItem extends ShapeItem {
  declare _fabricObject: PMWFabricLine;
  public gitype: ITEM_TYPE.LINE = ITEM_TYPE.LINE;

  override get fabricObject(): PMWFabricLine {
    return this._fabricObject;
  }

  public override async isPDFSafe(): Promise<boolean> {
    return false;
  }

  public override getFillSurface(): FillSurface {
    return FillSurface.SHAPE;
  }

  public override toObject(): LineItemObject {
    return {
      ...super.toObject(),
      lineDashType: this.fabricObject.lineDashType,
      lineCapStart: this.fabricObject.lineCapStart,
      lineCapEnd: this.fabricObject.lineCapEnd,
      customGapSize: this.fabricObject.customGapSize,
      customDashSize: this.fabricObject.customDashSize,
      isRounded: this.fabricObject.isRounded,
    };
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      frontFill: this.getFillObject(),
      lineDashType: this.toObject().lineDashType,
      lineCapStart: this.toObject().lineCapStart,
      lineCapEnd: this.toObject().lineCapEnd,
      customGapSize: this.toObject().customGapSize,
      customDashSize: this.toObject().customDashSize,
      isRounded: this.toObject().isRounded,
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<LineItemObject> = {};
    if (copiedStyles.frontFill !== undefined) {
      stylesObj.fill = this.getMovedFill(copiedStyles.frontFill);
    }
    if (copiedStyles.lineCapStart) {
      stylesObj.lineCapStart = copiedStyles.lineCapStart;
    }
    if (copiedStyles.lineCapEnd) {
      stylesObj.lineCapEnd = copiedStyles.lineCapEnd;
    }
    if (copiedStyles.isRounded) {
      stylesObj.isRounded = copiedStyles.isRounded;
    }
    if (copiedStyles.lineDashType) {
      stylesObj.lineDashType = copiedStyles.lineDashType;
      if (copiedStyles.lineDashType === LineDashType.CUSTOM) {
        if (copiedStyles.customDashSize && copiedStyles.customGapSize) {
          stylesObj.customDashSize = copiedStyles.customDashSize;
          stylesObj.customGapSize = copiedStyles.customGapSize;
        }
      }
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY', 'border']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  public override hasClickableLink(): boolean {
    return false;
  }

  protected override async updateFabricObjectFromObject(obj: DeepPartial<LineItemObject>): Promise<void> {
    await super.updateFabricObjectFromObject(obj);

    if (this.doesChangeMoveTheBoxFill(obj)) {
      this.drawBoxFill();
    }
    if (obj.lineDashType !== undefined) {
      this.fabricObject.set('lineDashType', obj.lineDashType);
    }
    if (obj.lineCapStart !== undefined) {
      this.fabricObject.set('lineCapStart', obj.lineCapStart);
    }
    if (obj.lineCapEnd !== undefined) {
      this.fabricObject.set('lineCapEnd', obj.lineCapEnd);
    }
    if (obj.isRounded !== undefined) {
      this.fabricObject.set('isRounded', obj.isRounded);
    }
    if (obj.customDashSize !== undefined) {
      this.fabricObject.set('customDashSize', obj.customDashSize);
    }

    if (obj.customGapSize !== undefined) {
      this.fabricObject.set('customGapSize', obj.customGapSize);
    }
  }
  protected override async getFabricObjectForItem(opts?: InitItemOpts): Promise<PMWFabricLine> {
    const {shadow} = await util.enlivenObjectEnlivables({
      shadow: opts?.shadow ?? null,
    });

    const line = new PMWFabricLine({
      width: opts?.width ?? DEFAULT_LINE_WIDTH,
      height: opts?.height ?? DEFAULT_HEIGHT,
      uniformScaling: false,
      uniformRoundness: true,
      scaleX: opts?.scaleX ?? 1,
      scaleY: opts?.scaleY ?? 1,
      padding: LINE_ITEM_PADDING,
    });

    line.set('shadow', shadow);
    return line;
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.fabricObject.set({
      padding: LINE_ITEM_PADDING,
    });
  }
}
